import { assert_string_object_id, assert_type_enum } from '@shared/asserts/utils_asserts';
import { assert_string_uuid } from '@shared/asserts/utils_asserts_string';
import { DTO__base } from '@shared/common/DTO__base';
import { ENUM__ml_asset_family } from '@shared/dto/+model/sd/_enums/ENUM__ml_asset_family';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { MAP__fields_by_id, MAP__fields_by_role } from '@shared/types/types';
import { timestamp_ms } from '@shared/utils/utils_time';
import { ENUM__field_store_interaction } from '../../_fields/_base/_enums/ENUM__field_store_interaction';
import { CONSTRUCTOR_FIELD_CONTAINER } from '../../_fields/_base/CONSTRUCTOR_FIELD_CONTAINER';
import { FIELD__base } from '../../_fields/_base/FIELD__base';
import { FIELD_PRIMITIVE__string_hash_ml_asset } from '../../_fields/_primitives/string/hash/FIELD_PRIMITIVE__string_hash_ml_asset';
import { FIELD__image_asset } from '../../_fields/image/FIELD__image_asset';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { INTERFACE_XYFLOW__node } from '../../_xyflow/INTERFACE_XYFLOW__node';
import { DTO_META__invocation_info } from './DTO_META__invocation_info';
import { ENUM__invocation_role } from './ENUM__invocation_role';
import { ENUM__invocation_type } from './ENUM__invocation_type';
import { FN_INVOCATION__create } from './FN_INVOCATION__create';

// todo:move to file
export interface CONSTRUCTOR_INVOCATION<T extends INVOCATION__base> extends CONSTRUCTOR_FIELD_CONTAINER<T> {
  create_default_meta(): DTO_META__invocation_info;
}

export class INVOCATION__base extends DTO__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static: string = ENUM__dto_class_name.ABSTRACT;

  /*
   * STATIC METHODS
   */

  static create_node_for<T extends INVOCATION__base>(
    this: new () => T,
    graph: DTO_GRAPH__instance,
    node_role: ENUM__invocation_role = ENUM__invocation_role.UNKNOWN,
  ): T {
    return FN_INVOCATION__create(this, graph, node_role);
  }

  static create_default_meta(): DTO_META__invocation_info {
    let out = new DTO_META__invocation_info();
    out.use_cache = true;
    out.is_deletable = true;

    out.create_id();

    return out;
  }

  static sort_by_added_at(a: INVOCATION__base, b: INVOCATION__base) {
    return a.meta.added_at - b.meta.added_at;
  }

  /*
   * FIELDS
   */
  private _fields_map_cache: MAP__fields_by_id;

  meta: DTO_META__invocation_info;

  type!: ENUM__invocation_type;

  /*
   * PROPERTIES
   */

  get_fields_map_by_name(): MAP__serializable<string, FIELD__base> {
    let out = new MAP__serializable<string, FIELD__base>();

    for (const [field_name, field] of Object.entries(this)) {
      if (!(field instanceof FIELD__base)) continue;

      out.set(field_name, field);
    }

    return out;
  }

  //key - field id
  get_fields_map(with_children: boolean): MAP__fields_by_id {
    if (with_children && !this._fields_map_cache.is_empty()) return this._fields_map_cache;

    let out = this.fields_map__create(with_children);

    return out;
  }

  fields_map_cache__init() {
    this._fields_map_cache = this.fields_map__create(true);
  }

  fields_map_cache__clear() {
    this._fields_map_cache = new MAP__serializable();
  }

  private fields_map__create(with_children: boolean) {
    let out: MAP__fields_by_id = new MAP__serializable();

    for (const [_, field] of Object.entries(this)) {
      if (!(field instanceof FIELD__base)) continue;

      out.set(field.meta.field_id, field);

      if (with_children) out.merge_with(field.fields__get_map(true));
    }

    return out;
  }

  get family(): ENUM__ml_asset_family {
    return ENUM__ml_asset_family.NONE;
  }

  is_grid2d_dimension_in_incomers(graph: DTO_GRAPH__instance) {
    let nodes_incomers = graph.nodes__get_map_incomers(this, false);
    let nodes_ids_incomers = nodes_incomers.values().map(node => node.meta.node_id);

    let grid2d_row = graph.grid2d__get_field_row();
    let grid2d_column = graph.grid2d__get_field_column();

    let out = false;

    if (nodes_ids_incomers.includes(grid2d_row.meta.node_id)) out = true;

    if (nodes_ids_incomers.includes(grid2d_column.meta.node_id)) out = true;

    return out;
  }

  /*
   * EVENTS
   */

  edges__try_autocreate(graph: DTO_GRAPH__instance) {}

  /*
   * METHODS
   */

  constructor(type: ENUM__invocation_type) {
    super();

    this.meta = DTO_META__invocation_info.CREATE_STUB();

    this.type = type;

    this._fields_map_cache = new MAP__serializable();

    assert_type_enum(this.type, ENUM__invocation_type);
  }

  post_init() {}

  update_fields_aliases(fields_by_role: MAP__fields_by_role) {}

  prepare_for_usage_in(graph: DTO_GRAPH__instance) {}

  fill_metadata(graph: DTO_GRAPH__instance) {
    assert_string_object_id(graph.meta.graph_id);
    assert_string_uuid(this.meta.node_id);
    assert_type_enum(this.type, ENUM__invocation_type);

    this.meta.graph_id = graph.meta.graph_id;
    this.meta.added_at = timestamp_ms();

    [this.meta.graph_x_px, this.meta.graph_y_px] = [0, 0];

    let fields_map_by_name_top_level = this.get_fields_map_by_name();

    for (const [field_name, field_top_level] of fields_map_by_name_top_level.entries()) {
      if (!(field_top_level instanceof FIELD__base)) continue;

      this.fill_metadata_for_top_level_field(field_top_level, field_name);
    }

    this.calculate_node_graph_size();
  }

  fill_metadata_for_top_level_field(field: FIELD__base, field_name: string) {
    field.fill_field_name_and_path(field_name, field_name);

    field.meta.graph_id = this.meta.graph_id;

    [field.meta.node_id, field.meta.node_type, field.meta.node_family] = [this.meta.node_id, this.type, this.family];

    assert_string_uuid(field.meta.field_id);

    let fields_children = field.fields__get_map(true).values();
    for (let field_child of fields_children) field_child.copy_meta_from_parent(field);
  }

  calculate_node_graph_size() {
    this.meta.graph_width = 30;

    let height = 0;

    height += 2; //node title
    height += 1; //footer

    let fields_map_inputs = this.fields__get_visible_in_graph_node_inputs();
    if (fields_map_inputs.size() > 0) height += 2 + 1; //2 for block title + 1 for pad

    let fields_map_outputs = this.fields__get_visible_in_graph_node_outputs();
    if (fields_map_outputs.size() > 0) height += 2 + 1; //2 for block title + 1 for pad

    let fields_map_all: MAP__fields_by_id = new MAP__serializable();
    fields_map_all.merge_with(fields_map_inputs);
    fields_map_all.merge_with(fields_map_outputs);

    for (let field of fields_map_all.values()) {
      height += field.graph_field__height;
    }

    this.meta.graph_height = height;
  }

  to_xyflow_format(): INTERFACE_XYFLOW__node {
    let out: INTERFACE_XYFLOW__node = {
      id: this.meta.node_id,
      type: 'invocation',
      position: { x: this.meta.graph_x_px, y: this.meta.graph_y_px },
      data: this as unknown as Record<string, unknown>,
      deletable: this.meta.is_deletable,
    };

    return out;
  }

  field_image__get_mask_for(field: FIELD__image_asset) {
    return FIELD__image_asset.CREATE_STUB();
  }

  field_image__get_under_mask_for(field: FIELD__image_asset) {
    return FIELD__image_asset.CREATE_STUB();
  }

  has_effect_on_generation_runtime(graph: DTO_GRAPH__instance) {
    if (!this.meta.is_connected_to_grid2d) return false;

    /*
     *
     */

    let fields_inputs = this.get_fields_map(false).values();

    let out = true;

    for (let field of fields_inputs) {
      if (field.meta.kind__is_output) continue;
      if (!field.meta.data__is_required) continue;

      let field_source = field.find_source(graph);
      if (field_source.has_effect_on_generation_runtime(graph)) continue;

      out = false;
      break;
    }

    return out;
  }

  store__disable() {
    let fields_map = this.get_fields_map(true);
    for (const field of fields_map.values()) {
      field.meta.store__interaction = ENUM__field_store_interaction.NONE;
    }
  }

  clear_field_roles() {
    let fields_map = this.get_fields_map(true);
    for (const field of fields_map.values()) {
      field.meta.field_role = '';
    }
  }

  fields_ids__get_wanted_to_be_filled(): string[] {
    /*
     *
     */

    let fields_ml_asset = this.fields__get_by_type(FIELD_PRIMITIVE__string_hash_ml_asset);
    fields_ml_asset = fields_ml_asset.filter(field => !field.is_stub()); //skip stubs
    fields_ml_asset = fields_ml_asset.filter(field => !field.meta.data__is_linked); //skip linked
    fields_ml_asset = fields_ml_asset.filter(field => field.get_value().is_empty_hash_sha256()); //keep only empties
    fields_ml_asset = fields_ml_asset.filter(field => field.meta.data__is_required); //skip optional

    let fields_ml_asset_ids = fields_ml_asset.map(field => field.meta.field_id);

    /*
     *
     */

    let fields_images = this.fields__get_by_type(FIELD__image_asset);
    fields_images = fields_images.filter(field => !field.is_stub()); //skip stubs
    fields_images = fields_images.filter(field => field.meta.kind__is_input); //keep only inputs
    fields_images = fields_images.filter(field => !field.meta.data__is_linked); //skip linked
    fields_images = fields_images.filter(field => !field.get_has_content()); //skip with content
    fields_images = fields_images.filter(field => field.meta.data__is_required); //skip optional

    let fields_images_ids = fields_images.map(field => field.meta.field_id);

    /*
     *
     */
    let out = [...fields_ml_asset_ids, ...fields_images_ids];

    return out;
  }

  fields_data__reset_if_not_linked(graph: DTO_GRAPH__instance) {
    let fields_list = this.get_fields_map(true).values();
    fields_list = fields_list.filter(field => field.meta.data__reset_if_not_linked);
    fields_list = fields_list.filter(field => !field.meta.data__is_linked);

    for (let field of fields_list) {
      field.data_reset(graph);
    }
  }

  /*
   * ASSERTS
   */

  assert_fields_ids(fields_ids: string[]) {
    let fields_ids_set = new Set(fields_ids);

    let fields_map = this.get_fields_map(true);

    for (const field_id of fields_ids_set) fields_map.get_or_assert(field_id);
  }

  assert_standard_fields(): void {
    assert_type_enum(this.type, ENUM__invocation_type);
  }
}
